import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import ClassCalendar from "../components/ClassCalendar";

const DAYS_OPTIONS = [
  { id: 1, label: "Mon" },
  { id: 2, label: "Tue" },
  { id: 3, label: "Wed" },
  { id: 4, label: "Thu" },
  { id: 5, label: "Fri" },
  { id: 6, label: "Sat" },
  { id: 0, label: "Sun" }
];

export default function TutorCalendarPage({ apiBase = "", token = "" }) {
  const location = useLocation();
  const [sessions, setSessions] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());

  // Filters
  const [filterClassroomId, setFilterClassroomId] = useState("all");
  const [filterSessionType, setFilterSessionType] = useState("all");

  // Modal State
  const [modalMode, setModalMode] = useState(null); // 'schedule' | 'reschedule' | 'cancel'
  const [activeSession, setActiveSession] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Form State
  const [formTitle, setFormTitle] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formSessionType, setFormSessionType] = useState("group");
  const [formClassroomId, setFormClassroomId] = useState("");
  const [groupAssignAll, setGroupAssignAll] = useState(true);
  const [selectedGroupStudentUids, setSelectedGroupStudentUids] = useState([]);
  const [formStudentUid, setFormStudentUid] = useState("");
  const [formDate, setFormDate] = useState("");
  const [formTime, setFormTime] = useState("17:00");
  const [formDuration, setFormDuration] = useState(60);
  const [formMeetingUrl, setFormMeetingUrl] = useState("");
  const [formVenue, setFormVenue] = useState("");

  // Recurring options (up to 12 weeks)
  const [isRecurring, setIsRecurring] = useState(false);
  const [repeatFrequency, setRepeatFrequency] = useState("weekly");
  const [repeatDays, setRepeatDays] = useState([1, 3, 5]); // Mon, Wed, Fri by default
  const [repeatWeeks, setRepeatWeeks] = useState(4); // 1-12

  // Cancel state
  const [cancelReason, setCancelReason] = useState("");
  const [cancelAllRecurring, setCancelAllRecurring] = useState(false);

  // Active classroom & candidate students for group dependent dropdown
  const activeClassroom = useMemo(() => {
    return classrooms.find((c) => String(c._id) === String(formClassroomId)) || null;
  }, [classrooms, formClassroomId]);

  const classroomStudents = useMemo(() => {
    if (!activeClassroom || !Array.isArray(activeClassroom.studentUids)) return [];
    const uidSet = new Set(activeClassroom.studentUids.map(String));
    return students.filter((st) => uidSet.has(String(st.studentUid || st.uid)));
  }, [activeClassroom, students]);

  const handleClassroomChange = (clsId) => {
    setFormClassroomId(clsId);
    setGroupAssignAll(true);
    const cls = classrooms.find((c) => String(c._id) === String(clsId));
    setSelectedGroupStudentUids(cls?.studentUids ? [...cls.studentUids] : []);
  };

  const fetchInitialData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [sessionsRes, classesRes, studentsRes] = await Promise.all([
        fetch(`${apiBase}/api/tutor/sessions`, { headers }).then((r) => (r.ok ? r.json() : { sessions: [] })),
        fetch(`${apiBase}/api/tutor/classrooms`, { headers }).then((r) => (r.ok ? r.json() : { classrooms: [] })),
        fetch(`${apiBase}/api/tutor/students`, { headers }).then((r) => (r.ok ? r.json() : { students: [] }))
      ]);
      setSessions(sessionsRes.sessions || []);
      setClassrooms(classesRes.classrooms || []);
      setStudents(studentsRes.students || []);
    } catch (err) {
      console.error("Failed to load tutor calendar data:", err);
      setErrorMsg("Failed to load schedule data. Please refresh.");
    } finally {
      setLoading(false);
    }
  }, [apiBase, token]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // Open Schedule Modal
  const handleOpenSchedule = useCallback((date = new Date()) => {
    const d = new Date(date);
    const dateStr = d.toISOString().split("T")[0];
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(Math.floor(d.getMinutes() / 15) * 15).padStart(2, "0");

    setFormTitle("");
    setFormDescription("");
    setFormSessionType("group");
    const firstCls = classrooms[0] || null;
    const firstClsId = firstCls?._id || "";
    setFormClassroomId(firstClsId);
    setGroupAssignAll(true);
    setSelectedGroupStudentUids(firstCls?.studentUids ? [...firstCls.studentUids] : []);
    const firstStudentId = students[0]?.studentUid || students[0]?.uid || "";
    setFormStudentUid(firstStudentId);
    setFormDate(dateStr);
    setFormTime(`${hours}:${minutes}`);
    setFormDuration(60);
    setFormMeetingUrl("");
    setFormVenue("");
    setIsRecurring(false);
    setRepeatFrequency("weekly");
    setRepeatDays([d.getDay()]);
    setRepeatWeeks(4);
    setErrorMsg("");
    setSuccessMsg("");
    setModalMode("schedule");
  }, [classrooms, students]);

  // Handle deep-linked navigation from Roster or Classrooms
  useEffect(() => {
    if (location.state?.classroomId) {
      setFilterClassroomId(location.state.classroomId);
    }
    if (location.state?.studentUid) {
      handleOpenSchedule();
      setFormSessionType("one_on_one");
      setFormStudentUid(location.state.studentUid);
    }
  }, [location.state, handleOpenSchedule]);

  const handleOpenReschedule = (session) => {
    setActiveSession(session);
    const d = new Date(session.startTime);
    setFormDate(d.toISOString().split("T")[0]);
    setFormTime(
      `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`
    );
    setFormDuration(session.durationMinutes || 60);
    setFormMeetingUrl(session.meetingUrl || "");
    setFormVenue(session.venue || "");
    setErrorMsg("");
    setSuccessMsg("");
    setModalMode("reschedule");
  };

  const handleOpenCancel = (session) => {
    setActiveSession(session);
    setCancelReason("");
    setCancelAllRecurring(false);
    setErrorMsg("");
    setSuccessMsg("");
    setModalMode("cancel");
  };

  const handleCloseModal = () => {
    setModalMode(null);
    setActiveSession(null);
    setErrorMsg("");
  };

  const toggleRepeatDay = (dayId) => {
    setRepeatDays((prev) =>
      prev.includes(dayId) ? prev.filter((d) => d !== dayId) : [...prev, dayId]
    );
  };

  // Submit Schedule Session
  const handleSubmitSchedule = async (e) => {
    e.preventDefault();
    if (!formTitle.trim()) {
      setErrorMsg("Session title is required");
      return;
    }
    if (!formDate || !formTime) {
      setErrorMsg("Please select a date and start time");
      return;
    }

    setSaving(true);
    setErrorMsg("");

    try {
      const combinedStart = new Date(`${formDate}T${formTime}:00`);

      const payload = {
        title: formTitle.trim(),
        description: formDescription.trim(),
        sessionType: formSessionType,
        startTime: combinedStart.toISOString(),
        durationMinutes: Number(formDuration),
        meetingUrl: formMeetingUrl.trim(),
        venue: formVenue.trim()
      };

      if (formSessionType === "group") {
        payload.classroomId = formClassroomId || null;
        if (!groupAssignAll) {
          if (!selectedGroupStudentUids.length) {
            setErrorMsg("Please select at least one student from the group");
            setSaving(false);
            return;
          }
          payload.studentUids = selectedGroupStudentUids;
        } else {
          payload.studentUids = activeClassroom?.studentUids || [];
        }
      } else {
        if (!formStudentUid) {
          setErrorMsg("Please select a student for this 1:1 session");
          setSaving(false);
          return;
        }
        payload.studentUids = [formStudentUid];
        payload.classroomId = null;
      }

      if (isRecurring) {
        payload.recurrence = {
          frequency: repeatFrequency,
          daysOfWeek: repeatFrequency === "weekly" ? repeatDays : [],
          weeks: Math.min(12, Math.max(1, Number(repeatWeeks) || 1))
        };
      }

      const res = await fetch(`${apiBase}/api/tutor/sessions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Failed to schedule session");
      }

      setSuccessMsg(data.message || "Session scheduled successfully!");
      handleCloseModal();
      await fetchInitialData();
    } catch (err) {
      console.error("Schedule failed:", err);
      setErrorMsg(err.message || "Failed to schedule session");
    } finally {
      setSaving(false);
    }
  };

  // Submit Reschedule
  const handleSubmitReschedule = async (e) => {
    e.preventDefault();
    if (!activeSession) return;

    setSaving(true);
    setErrorMsg("");

    try {
      const combinedStart = new Date(`${formDate}T${formTime}:00`);
      const res = await fetch(`${apiBase}/api/tutor/sessions/${activeSession._id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          startTime: combinedStart.toISOString(),
          durationMinutes: Number(formDuration),
          meetingUrl: formMeetingUrl.trim(),
          venue: formVenue.trim()
        })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Failed to reschedule session");
      }

      setSuccessMsg("Session rescheduled successfully");
      handleCloseModal();
      await fetchInitialData();
    } catch (err) {
      console.error("Reschedule failed:", err);
      setErrorMsg(err.message || "Failed to reschedule session");
    } finally {
      setSaving(false);
    }
  };

  // Submit Cancel
  const handleSubmitCancel = async (e) => {
    e.preventDefault();
    if (!activeSession) return;

    setSaving(true);
    setErrorMsg("");

    try {
      const res = await fetch(`${apiBase}/api/tutor/sessions/${activeSession._id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          reason: cancelReason.trim(),
          cancelAllRecurring
        })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Failed to cancel session");
      }

      setSuccessMsg("Session cancelled");
      handleCloseModal();
      await fetchInitialData();
    } catch (err) {
      console.error("Cancel failed:", err);
      setErrorMsg(err.message || "Failed to cancel session");
    } finally {
      setSaving(false);
    }
  };

  // Filtered sessions
  const filteredSessions = useMemo(() => {
    return sessions.filter((s) => {
      if (filterClassroomId !== "all" && String(s.classroomId) !== filterClassroomId) {
        return false;
      }
      if (filterSessionType !== "all" && s.sessionType !== filterSessionType) {
        return false;
      }
      return true;
    });
  }, [sessions, filterClassroomId, filterSessionType]);

  // Today sessions count & distinct class series count
  const { todaySessionsCount, uniqueClassesCount } = useMemo(() => {
    const today = new Date();
    let todayCount = 0;
    const seriesKeys = new Set();

    filteredSessions.forEach((s) => {
      if (s.status === "cancelled" || !s.startTime) return;
      const d = new Date(s.startTime);
      if (
        d.getFullYear() === today.getFullYear() &&
        d.getMonth() === today.getMonth() &&
        d.getDate() === today.getDate()
      ) {
        todayCount++;
      }
      const key = s.recurrenceId ? `rec_${s.recurrenceId}` : `single_${s._id}`;
      seriesKeys.add(key);
    });

    return { todaySessionsCount: todayCount, uniqueClassesCount: seriesKeys.size };
  }, [filteredSessions]);

  return (
    <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "24px 20px" }}>
      {/* Page Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "16px", marginBottom: "20px" }}>
        <div>
          <h1 style={{ fontSize: "24px", fontWeight: "700", color: "#0f172a", margin: "0 0 6px 0" }}>
            Class & Session Calendar
          </h1>
          <p style={{ margin: 0, fontSize: "14px", color: "#64748b" }}>
            Plan, organize, and manage 1:1 private sessions and group classes.
          </p>
        </div>

        <button
          type="button"
          onClick={() => handleOpenSchedule(selectedDate)}
          style={{
            backgroundColor: "#2563eb",
            color: "#ffffff",
            padding: "10px 18px",
            borderRadius: "8px",
            fontWeight: "600",
            fontSize: "14px",
            border: "none",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: "8px"
          }}
        >
          <span>+</span> Schedule Class
        </button>
      </div>

      {/* Alerts */}
      {successMsg && (
        <div style={{ padding: "12px 16px", backgroundColor: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: "8px", color: "#065f46", fontSize: "14px", fontWeight: "600", marginBottom: "16px", display: "flex", justifyContent: "space-between" }}>
          <span>{successMsg}</span>
          <button type="button" onClick={() => setSuccessMsg("")} style={{ background: "none", border: "none", cursor: "pointer", color: "#065f46", fontWeight: "700" }}>✕</button>
        </div>
      )}

      {errorMsg && !modalMode && (
        <div style={{ padding: "12px 16px", backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px", color: "#b91c1c", fontSize: "14px", fontWeight: "600", marginBottom: "16px" }}>
          {errorMsg}
        </div>
      )}

      {/* Filter Bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          flexWrap: "wrap",
          padding: "12px 16px",
          backgroundColor: "#ffffff",
          borderRadius: "10px",
          border: "1px solid #e2e8f0",
          marginBottom: "20px"
        }}
      >
        <span style={{ fontSize: "13px", fontWeight: "700", color: "#475569" }}>Filter by:</span>

        {/* Classroom Filter */}
        <select
          value={filterClassroomId}
          onChange={(e) => setFilterClassroomId(e.target.value)}
          style={{
            padding: "6px 12px",
            borderRadius: "6px",
            border: "1px solid #cbd5e1",
            fontSize: "13px",
            backgroundColor: "#ffffff",
            color: "#1e293b"
          }}
        >
          <option value="all">All Batches & Classes</option>
          {classrooms.map((c) => (
            <option key={c._id} value={c._id}>
              {c.name} {c.classType === "one_on_one" ? "(1:1)" : "(Group)"}
            </option>
          ))}
        </select>

        {/* Session Type Filter */}
        <select
          value={filterSessionType}
          onChange={(e) => setFilterSessionType(e.target.value)}
          style={{
            padding: "6px 12px",
            borderRadius: "6px",
            border: "1px solid #cbd5e1",
            fontSize: "13px",
            backgroundColor: "#ffffff",
            color: "#1e293b"
          }}
        >
          <option value="all">All Types (1:1 & Group)</option>
          <option value="one_on_one">1:1 Private Sessions</option>
          <option value="group">Group Classes</option>
        </select>

        {(filterClassroomId !== "all" || filterSessionType !== "all") && (
          <button
            type="button"
            onClick={() => {
              setFilterClassroomId("all");
              setFilterSessionType("all");
            }}
            style={{
              padding: "4px 10px",
              fontSize: "12px",
              fontWeight: "600",
              color: "#64748b",
              backgroundColor: "#f1f5f9",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer"
            }}
          >
            Clear Filters
          </button>
        )}

        <div style={{ marginLeft: "auto", fontSize: "13px", color: "#64748b" }}>
          {todaySessionsCount > 0 && (
            <span style={{ fontWeight: "700", color: "#1d4ed8", marginRight: "6px" }}>
              {todaySessionsCount} today •
            </span>
          )}
          <span>
            <strong>{uniqueClassesCount}</strong> distinct {uniqueClassesCount === 1 ? "class" : "classes"} ({filteredSessions.length} scheduled {filteredSessions.length === 1 ? "slot" : "slots"})
          </span>
        </div>
      </div>

      {/* Main Calendar View */}
      <ClassCalendar
        sessions={filteredSessions}
        selectedDate={selectedDate}
        onSelectDate={(date) => setSelectedDate(date)}
        onScheduleClick={(date) => handleOpenSchedule(date)}
        onRescheduleClick={(session) => handleOpenReschedule(session)}
        onCancelClick={(session) => handleOpenCancel(session)}
        isTutor={true}
        loading={loading}
      />

      {/* ── MODAL: SCHEDULE SESSION ── */}
      {modalMode === "schedule" && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(15, 23, 42, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "16px"
          }}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "14px",
              width: "100%",
              maxWidth: "560px",
              maxHeight: "90vh",
              overflowY: "auto",
              boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)",
              padding: "24px"
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "18px" }}>
              <h2 style={{ margin: 0, fontSize: "18px", fontWeight: "700", color: "#0f172a" }}>
                Schedule Class Session
              </h2>
              <button
                type="button"
                onClick={handleCloseModal}
                style={{ background: "none", border: "none", fontSize: "20px", cursor: "pointer", color: "#94a3b8" }}
              >
                ✕
              </button>
            </div>

            {errorMsg && (
              <div style={{ padding: "10px 14px", backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: "6px", color: "#b91c1c", fontSize: "13px", marginBottom: "16px" }}>
                {errorMsg}
              </div>
            )}

            <form onSubmit={handleSubmitSchedule} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {/* Session Type */}
              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "700", color: "#475569", marginBottom: "6px" }}>
                  Class Type
                </label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                  <button
                    type="button"
                    onClick={() => setFormSessionType("group")}
                    style={{
                      padding: "10px 14px",
                      borderRadius: "8px",
                      border: formSessionType === "group" ? "2px solid #059669" : "1.5px solid #cbd5e1",
                      backgroundColor: formSessionType === "group" ? "#ecfdf5" : "#ffffff",
                      color: formSessionType === "group" ? "#065f46" : "#475569",
                      fontWeight: "700",
                      fontSize: "13px",
                      cursor: "pointer"
                    }}
                  >
                    Group Batch
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormSessionType("one_on_one")}
                    style={{
                      padding: "10px 14px",
                      borderRadius: "8px",
                      border: formSessionType === "one_on_one" ? "2px solid #7c3aed" : "1.5px solid #cbd5e1",
                      backgroundColor: formSessionType === "one_on_one" ? "#faf5ff" : "#ffffff",
                      color: formSessionType === "one_on_one" ? "#5b21b6" : "#475569",
                      fontWeight: "700",
                      fontSize: "13px",
                      cursor: "pointer"
                    }}
                  >
                    1:1 Private Tutoring
                  </button>
                </div>
              </div>

              {/* Title */}
              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "700", color: "#475569", marginBottom: "6px" }}>
                  Topic / Session Title *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Calculus: Derivatives Doubt Session"
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1.5px solid #cbd5e1", fontSize: "14px", boxSizing: "border-box" }}
                />
              </div>

              {/* Target Class or Student */}
              {formSessionType === "group" ? (
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: "700", color: "#475569", marginBottom: "6px" }}>
                    Select Group Batch *
                  </label>
                  <select
                    value={formClassroomId}
                    onChange={(e) => handleClassroomChange(e.target.value)}
                    style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1.5px solid #cbd5e1", fontSize: "14px", backgroundColor: "#ffffff" }}
                  >
                    <option value="">Select a classroom batch...</option>
                    {classrooms.map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.name} ({c.studentUids?.length || 0} enrolled)
                      </option>
                    ))}
                  </select>

                  {/* Dependent Student Selector for the Selected Group */}
                  {activeClassroom && (
                    <div style={{ marginTop: "12px", padding: "12px 14px", backgroundColor: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                      <div style={{ fontSize: "12px", fontWeight: "700", color: "#334155", marginBottom: "8px" }}>
                        Select Enrolled Students in {activeClassroom.name}
                      </div>
                      <div style={{ display: "flex", gap: "16px", marginBottom: "10px" }}>
                        <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", cursor: "pointer" }}>
                          <input
                            type="radio"
                            name="calendarGroupAssign"
                            checked={groupAssignAll}
                            onChange={() => {
                              setGroupAssignAll(true);
                              setSelectedGroupStudentUids(activeClassroom.studentUids || []);
                            }}
                          />
                          <span>Entire Group ({activeClassroom.studentUids?.length || 0} students)</span>
                        </label>
                        <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", cursor: "pointer" }}>
                          <input
                            type="radio"
                            name="calendarGroupAssign"
                            checked={!groupAssignAll}
                            onChange={() => setGroupAssignAll(false)}
                          />
                          <span>Select Specific Students</span>
                        </label>
                      </div>

                      {!groupAssignAll && (
                        <div style={{ maxHeight: "140px", overflowY: "auto", border: "1px solid #cbd5e1", borderRadius: "6px", padding: "6px 10px", backgroundColor: "#ffffff" }}>
                          {classroomStudents.length === 0 ? (
                            <div style={{ fontSize: "12px", color: "var(--muted)", padding: "6px 0" }}>
                              No enrolled students found in this batch.
                            </div>
                          ) : (
                            classroomStudents.map((st) => {
                              const sId = st.studentUid || st.uid;
                              const checked = selectedGroupStudentUids.includes(sId);
                              return (
                                <label key={sId} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", padding: "4px 0", cursor: "pointer" }}>
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedGroupStudentUids([...selectedGroupStudentUids, sId]);
                                      } else {
                                        setSelectedGroupStudentUids(selectedGroupStudentUids.filter((id) => id !== sId));
                                      }
                                    }}
                                  />
                                  <span>{st.name || st.username} {st.email ? `(${st.email})` : ""}</span>
                                </label>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: "700", color: "#475569", marginBottom: "6px" }}>
                    Select Student for 1:1 Session *
                  </label>
                  <select
                    value={formStudentUid}
                    onChange={(e) => setFormStudentUid(e.target.value)}
                    style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1.5px solid #cbd5e1", fontSize: "14px", backgroundColor: "#ffffff" }}
                  >
                    {students.length === 0 && <option value="">No students in roster</option>}
                    {students.map((st) => {
                      const sId = st.studentUid || st.uid;
                      return (
                        <option key={sId} value={sId}>
                          {st.name || st.username} ({st.email})
                        </option>
                      );
                    })}
                  </select>
                </div>
              )}

              {/* Date & Time */}
              <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 1fr", gap: "10px" }}>
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: "700", color: "#475569", marginBottom: "6px" }}>
                    Start Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={formDate}
                    onChange={(e) => setFormDate(e.target.value)}
                    style={{ width: "100%", padding: "8px 10px", borderRadius: "6px", border: "1.5px solid #cbd5e1", fontSize: "13px", boxSizing: "border-box" }}
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: "700", color: "#475569", marginBottom: "6px" }}>
                    Start Time *
                  </label>
                  <input
                    type="time"
                    required
                    value={formTime}
                    onChange={(e) => setFormTime(e.target.value)}
                    style={{ width: "100%", padding: "8px 10px", borderRadius: "6px", border: "1.5px solid #cbd5e1", fontSize: "13px", boxSizing: "border-box" }}
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: "700", color: "#475569", marginBottom: "6px" }}>
                    Duration
                  </label>
                  <select
                    value={formDuration}
                    onChange={(e) => setFormDuration(Number(e.target.value))}
                    style={{ width: "100%", padding: "8px 10px", borderRadius: "6px", border: "1.5px solid #cbd5e1", fontSize: "13px", backgroundColor: "#ffffff" }}
                  >
                    <option value={30}>30 min</option>
                    <option value={45}>45 min</option>
                    <option value={60}>60 min</option>
                    <option value={90}>90 min</option>
                    <option value={120}>120 min</option>
                  </select>
                </div>
              </div>

              {/* Online Meeting Link */}
              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "700", color: "#475569", marginBottom: "6px" }}>
                  Online Class Link (Google Meet / Zoom)
                </label>
                <input
                  type="url"
                  placeholder="https://meet.google.com/xyz-abc"
                  value={formMeetingUrl}
                  onChange={(e) => setFormMeetingUrl(e.target.value)}
                  style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1.5px solid #cbd5e1", fontSize: "13px", boxSizing: "border-box" }}
                />
              </div>

              {/* Recurring Booking Option */}
              <div style={{ backgroundColor: "#f8fafc", padding: "14px", borderRadius: "8px", border: "1.5px solid #e2e8f0" }}>
                <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontWeight: "700", fontSize: "13px", color: "#1e293b" }}>
                  <input
                    type="checkbox"
                    checked={isRecurring}
                    onChange={(e) => setIsRecurring(e.target.checked)}
                    style={{ width: "16px", height: "16px" }}
                  />
                  Repeat this session (Recurring series up to 12 weeks)
                </label>

                {isRecurring && (
                  <div style={{ marginTop: "12px", display: "flex", flexDirection: "column", gap: "10px" }}>
                    <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                      <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>Frequency:</label>
                      <select
                        value={repeatFrequency}
                        onChange={(e) => setRepeatFrequency(e.target.value)}
                        style={{ padding: "4px 8px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "12px" }}
                      >
                        <option value="weekly">Weekly</option>
                        <option value="daily">Daily</option>
                      </select>

                      <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569", marginLeft: "12px" }}>Duration:</label>
                      <select
                        value={repeatWeeks}
                        onChange={(e) => setRepeatWeeks(Number(e.target.value))}
                        style={{ padding: "4px 8px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "12px" }}
                      >
                        {[1, 2, 3, 4, 6, 8, 10, 12].map((w) => (
                          <option key={w} value={w}>
                            {w} {w === 1 ? "week" : "weeks"}
                          </option>
                        ))}
                      </select>
                    </div>

                    {repeatFrequency === "weekly" && (
                      <div>
                        <div style={{ fontSize: "12px", fontWeight: "600", color: "#475569", marginBottom: "4px" }}>
                          Repeat on days:
                        </div>
                        <div style={{ display: "flex", gap: "6px" }}>
                          {DAYS_OPTIONS.map((day) => {
                            const isSelected = repeatDays.includes(day.id);
                            return (
                              <button
                                key={day.id}
                                type="button"
                                onClick={() => toggleRepeatDay(day.id)}
                                style={{
                                  padding: "4px 8px",
                                  fontSize: "12px",
                                  fontWeight: "700",
                                  borderRadius: "6px",
                                  border: isSelected ? "1.5px solid #2563eb" : "1px solid #cbd5e1",
                                  backgroundColor: isSelected ? "#eff6ff" : "#ffffff",
                                  color: isSelected ? "#1d4ed8" : "#64748b",
                                  cursor: "pointer"
                                }}
                              >
                                {day.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div style={{ fontSize: "11px", color: "#64748b", backgroundColor: "#ffffff", padding: "6px 10px", borderRadius: "6px", border: "1px solid #e2e8f0" }}>
                      Will schedule a {repeatFrequency} series for {repeatWeeks} weeks max. After 12 weeks, you can easily renew with a fresh booking.
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "8px" }}>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  style={{ padding: "8px 16px", borderRadius: "6px", border: "1px solid #cbd5e1", backgroundColor: "#ffffff", color: "#475569", fontWeight: "600", fontSize: "13px", cursor: "pointer" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  style={{ padding: "8px 20px", borderRadius: "6px", border: "none", backgroundColor: "#2563eb", color: "#ffffff", fontWeight: "700", fontSize: "13px", cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1 }}
                >
                  {saving ? "Scheduling..." : isRecurring ? "Schedule Recurring Series" : "Schedule Session"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── MODAL: RESCHEDULE SESSION ── */}
      {modalMode === "reschedule" && activeSession && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(15, 23, 42, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "16px"
          }}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "14px",
              width: "100%",
              maxWidth: "480px",
              padding: "24px",
              boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)"
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <h2 style={{ margin: 0, fontSize: "18px", fontWeight: "700", color: "#0f172a" }}>
                Reschedule Session
              </h2>
              <button
                type="button"
                onClick={handleCloseModal}
                style={{ background: "none", border: "none", fontSize: "20px", cursor: "pointer", color: "#94a3b8" }}
              >
                ✕
              </button>
            </div>

            <div style={{ padding: "10px 14px", backgroundColor: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0", marginBottom: "16px" }}>
              <div style={{ fontWeight: "700", color: "#0f172a", fontSize: "14px" }}>{activeSession.title}</div>
              <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
                {activeSession.sessionType === "one_on_one" ? "1:1 Private" : "Group Class"}
              </div>
            </div>

            {errorMsg && (
              <div style={{ padding: "8px 12px", backgroundColor: "#fef2f2", color: "#b91c1c", borderRadius: "6px", fontSize: "13px", marginBottom: "14px" }}>
                {errorMsg}
              </div>
            )}

            <form onSubmit={handleSubmitReschedule} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: "10px" }}>
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: "700", color: "#475569", marginBottom: "6px" }}>
                    New Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={formDate}
                    onChange={(e) => setFormDate(e.target.value)}
                    style={{ width: "100%", padding: "8px 10px", borderRadius: "6px", border: "1.5px solid #cbd5e1", fontSize: "13px", boxSizing: "border-box" }}
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: "700", color: "#475569", marginBottom: "6px" }}>
                    New Time *
                  </label>
                  <input
                    type="time"
                    required
                    value={formTime}
                    onChange={(e) => setFormTime(e.target.value)}
                    style={{ width: "100%", padding: "8px 10px", borderRadius: "6px", border: "1.5px solid #cbd5e1", fontSize: "13px", boxSizing: "border-box" }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "700", color: "#475569", marginBottom: "6px" }}>
                  Duration
                </label>
                <select
                  value={formDuration}
                  onChange={(e) => setFormDuration(Number(e.target.value))}
                  style={{ width: "100%", padding: "8px 10px", borderRadius: "6px", border: "1.5px solid #cbd5e1", fontSize: "13px", backgroundColor: "#ffffff" }}
                >
                  <option value={30}>30 min</option>
                  <option value={45}>45 min</option>
                  <option value={60}>60 min</option>
                  <option value={90}>90 min</option>
                  <option value={120}>120 min</option>
                </select>
              </div>

              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "700", color: "#475569", marginBottom: "6px" }}>
                  Meeting URL
                </label>
                <input
                  type="url"
                  value={formMeetingUrl}
                  onChange={(e) => setFormMeetingUrl(e.target.value)}
                  style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1.5px solid #cbd5e1", fontSize: "13px", boxSizing: "border-box" }}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "8px" }}>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  style={{ padding: "8px 16px", borderRadius: "6px", border: "1px solid #cbd5e1", backgroundColor: "#ffffff", color: "#475569", fontWeight: "600", fontSize: "13px", cursor: "pointer" }}
                >
                  Close
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  style={{ padding: "8px 20px", borderRadius: "6px", border: "none", backgroundColor: "#2563eb", color: "#ffffff", fontWeight: "700", fontSize: "13px", cursor: saving ? "not-allowed" : "pointer" }}
                >
                  {saving ? "Saving..." : "Save Reschedule"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── MODAL: CANCEL SESSION ── */}
      {modalMode === "cancel" && activeSession && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(15, 23, 42, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "16px"
          }}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "14px",
              width: "100%",
              maxWidth: "460px",
              padding: "24px",
              boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)"
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <h2 style={{ margin: 0, fontSize: "18px", fontWeight: "700", color: "#dc2626" }}>
                Cancel Session
              </h2>
              <button
                type="button"
                onClick={handleCloseModal}
                style={{ background: "none", border: "none", fontSize: "20px", cursor: "pointer", color: "#94a3b8" }}
              >
                ✕
              </button>
            </div>

            <p style={{ margin: "0 0 14px 0", fontSize: "14px", color: "#334155" }}>
              Are you sure you want to cancel <strong>{activeSession.title}</strong>? Students will be notified.
            </p>

            {Boolean(activeSession.recurrenceId) && (
              <div style={{ backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px", padding: "12px", marginBottom: "14px" }}>
                <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "13px", fontWeight: "700", color: "#991b1b" }}>
                  <input
                    type="checkbox"
                    checked={cancelAllRecurring}
                    onChange={(e) => setCancelAllRecurring(e.target.checked)}
                    style={{ width: "16px", height: "16px" }}
                  />
                  Cancel this AND all future sessions in this recurring series
                </label>
              </div>
            )}

            <form onSubmit={handleSubmitCancel} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "700", color: "#475569", marginBottom: "6px" }}>
                  Cancellation Reason (optional)
                </label>
                <textarea
                  rows={2}
                  placeholder="e.g. Rescheduled due to teacher illness"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1.5px solid #cbd5e1", fontSize: "13px", boxSizing: "border-box" }}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "6px" }}>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  style={{ padding: "8px 16px", borderRadius: "6px", border: "1px solid #cbd5e1", backgroundColor: "#ffffff", color: "#475569", fontWeight: "600", fontSize: "13px", cursor: "pointer" }}
                >
                  Keep Session
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  style={{ padding: "8px 20px", borderRadius: "6px", border: "none", backgroundColor: "#dc2626", color: "#ffffff", fontWeight: "700", fontSize: "13px", cursor: saving ? "not-allowed" : "pointer" }}
                >
                  {saving ? "Cancelling..." : "Confirm Cancel"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

